export type ImpactedEntityType = "VARIANT" | "PRODUCT" | "COLLECTION";

export interface ImpactedEntity {
  type: ImpactedEntityType;
  entity_ids: string[];
  missing_quantity: number | null;
  missing_amount: number | null;
}

export interface DiscountAchieverVariant {
  merchant_id: string;
  variant_id: string;
  name: string;
  sku: string | null;
  current_price: number;
  original_price: number;
  discounted_price: number | null;
  currency: string;
  inventory_quantity: number;
  inventory_policy: string;
  out_of_stock: boolean;
  inventory_management: null | any;
  imageUrl?: string;
}
/** Inventory cap only applies when the merchant blocks overselling for this variant */
export const isInventoryCapped = (variant: DiscountAchieverVariant): boolean =>
  variant.inventory_policy === "DENY" && variant.inventory_management != null;

/** Effective add cap — Infinity when overselling is allowed, else the on-hand quantity */
export const getInventoryCap = (variant: DiscountAchieverVariant): number =>
  isInventoryCapped(variant) ? variant.inventory_quantity : Infinity;
export interface DiscountAchieverImage {
  image_id: string;
  src: string;
  alt: string;
  is_primary: boolean;
  linked_variant_ids: string[];
}

export interface DiscountAchieverProduct {
  merchant_id: string;
  product_id: string;
  name: string;
  description: string;
  product_type: string;
  status: string;
  variants: DiscountAchieverVariant[];
  images: DiscountAchieverImage[];
}

/**
 * One entry of the variant lookup response (`.../variant` and `.../variant/bulk`) — a variant
 * with its parent product inlined, so a single variant id resolves the product's full sibling set.
 */
export interface VariantCatalogEntry extends DiscountAchieverVariant {
  product: DiscountAchieverProduct;
}

export type SortBy = "PRICE_ASC" | "PRICE_DESC" | "POPULARITY" | "GAP_CLOSING";

export interface PriceBand {
  min: number;
  max: number;
}

export interface BlockFilter {
  exclude_out_of_stock?: boolean;
  exclude_variant_ids?: string[];
  search?: string;
  selected_price_band?: PriceBand;
}

export interface BlockPagination {
  next_cursor: string | null;
  has_more: boolean;
  page_size: number;
  total?: number;
}

export interface DiscountAchieverBlock {
  impacted_entity: ImpactedEntity;
  products: DiscountAchieverProduct[];
  pagination: BlockPagination;
}

export interface ResolveDiscountAchieverRequest {
  impacted_entities: ImpactedEntity[];
  sort_by: SortBy;
  filter: BlockFilter;
  page_size: number;
}

export interface ResolveDiscountAchieverResponse {
  blocks: DiscountAchieverBlock[];
}

export interface ResolveDiscountAchieverPageRequest {
  cursor: string;
}

export interface ResolveDiscountAchieverPageResponse {
  products: DiscountAchieverProduct[];
  pagination: BlockPagination;
}

export interface BlockNavRailItem {
  id: string;
  thumbnail: string;
  label: string;
  current: number;
  target: number;
  /** Quantity-based blocks show the "current/target" badge — amount-based blocks have no meaningful count */
  showCount: boolean;
}

export interface BlockNavRailProps {
  items: BlockNavRailItem[];
  activeId: string;
  onSelect: (id: string) => void;
}
